import { Button, LoadingArea, Position, Search, Spinner } from '@consigli/facade';
import { useMyUser } from '@consigli/hooks';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { HiOutlineFolder, HiOutlinePlusSm } from 'react-icons/hi';
import { useNavigate } from 'react-router';

import { Breadcrumbs } from '@/features/breadcrumb/breadcrumbs';
import { CenteredLayout } from '@/layouts/centered-content';
import { FluidLayout } from '@/layouts/fluid-content';
import { Route } from '@/routes';
import { formatDisplayName } from '@/util/format-name';

import { ProjectCards } from './project-cards';
import { useProjectList } from './use-project-list';

export const ProjectListPage: FC = () => {
  const navigate = useNavigate();
  const { user } = useMyUser();
  const { t } = useTranslation();
  const {
    projects,
    isLoading,
    isFetchingMore,
    isSearchPending,
    searchText,
    handleSearch,
    triggerRef,
  } = useProjectList();

  const NewProjectButton: FC<{ className?: string }> = ({ className = '' }) => (
    <Button
      rounded
      primary
      className={className}
      onClick={() => navigate(`/${Route.NEW_PROJECT}`)}
      icon={HiOutlinePlusSm}
      iconSize={23}
      iconPosition={Position.RIGHT}
      iconColor={'white'}
    >
      {t('project-creation.new-project')}
    </Button>
  );

  return (
    <FluidLayout>
      <div className="flex border-b border-day-secondary flex-col sm:flex-row items-center justify-between">
        <div className="flex items-center justify-center sm:justify-start p-3 px-14">
          <Breadcrumbs />
        </div>
        <div className="flex w-full flex-col sm:flex-row justify-center sm:justify-end items-center p-3 gap-x-2">
          <Search
            text={searchText}
            onChange={handleSearch}
            placeholder={t('project-list.search-projects')}
          />
          {user.organizationId && projects.length > 0 && (
            <NewProjectButton className="mt-4 sm:mt-0 mx-4 px-4 text-base font-semibold" />
          )}
        </div>
      </div>
      <CenteredLayout>
        <div
          data-testid="welcome-heading"
          className="text-[2rem] font-semibold capitalize text-center text-day-neutral-dark pt-4"
        >
          {t('project-list.welcome')} {formatDisplayName(user.firstName, user.lastName)}!
        </div>
        <p
          data-testid="project-overview-text"
          className="text-base p-4 text-center text-day-neutral-dark pt-4 pb-8"
        >
          {t('project-list.your-projects')}
        </p>
        {isLoading || (projects.length === 0 && isSearchPending) ? (
          <div className="flex justify-center items-center">
            <LoadingArea title={t('project-list.loading-title')} className="mx-auto mt-[25vh]" />
          </div>
        ) : (
          <>
            {projects.length > 0 && (
              <div className="px-14 pb-8">
                <ProjectCards projects={projects} />

                {/* Infinite scroll trigger */}
                <div ref={triggerRef} className="h-4" />

                {isFetchingMore && (
                  <div className="flex justify-center items-center py-8">
                    <Spinner size="large" />
                  </div>
                )}
              </div>
            )}

            {/* The redesign branches the same three cases in project-list-empty.tsx,
                against split title/description keys. This block is the only thing
                still reading no-projects-found and no-projects-match-current-search,
                so both go dead in all 11 locales when this page is deleted with the
                flag. Nothing will flag them, so remove them at the same time. */}
            {projects.length === 0 && !isLoading && (
              <div className="px-14 pb-8">
                {searchText ? (
                  <div className="m-8 text-center text-lg">
                    {t('project-list.no-projects-match-current-search')}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center mt-16">
                    <div className="mb-8 flex items-center justify-center w-30 h-30 rounded-full bg-gray-100">
                      <HiOutlineFolder className="text-gray-300" size={60} />
                    </div>
                    <div className="text-lg text-day-neutral-dark mb-8 text-center max-w-md">
                      {user.organizationId
                        ? t('project-list.no-projects-found')
                        : t('project-list.no-projects-access-denied')}
                    </div>
                    {user.organizationId && (
                      <NewProjectButton className="px-6 py-3 text-base font-semibold" />
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </CenteredLayout>
    </FluidLayout>
  );
};
